/* eslint-disable no-console */
import React, {ReactElement, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {cx} from '@emotion/css';
import {Formik} from 'formik';
import {navigate} from 'gatsby';
import {useFlags} from 'launchdarkly-react-client-sdk';

import BaseModal from '@fo/designSystem/BaseModal/BaseModal';
import useLogin from '@fo/shared-data/identity/useLogin/useLogin';
import EmailAddressField from '@fo/designSystem/Fields/Email/EmailWithFormikField';
import PasswordField from '@fo/designSystem/Fields/Password/PasswordWithFormikField';
import SinglePageForm from '@fo/designSystem/SinglePageForm/SinglePageForm';
import {IdentityChallenge} from '@fo/shared-types';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import {Login} from '@fo/shared-data/apis/Identity';
import redirectByQueryParam from '@fo/utils/redirectByQueryParam';
import Button, {ButtonVariant} from '@fo/components/DesignSystem/Buttons/Button/Button';
import {TIDE_WEB_APP_BASE_ADDRESS} from '@fo/shared-types/constants';
import redirectByBRSQuery from '@fo/utils/redirectToBRSQueryParams';
import {ArrowWithLineLargeIcon} from '@fo/root/designSystem/atoms/Icons';
import {IconRotation} from '@fo/root/designSystem/atoms/Icons/types';
import {isBrowser} from '@fo/shared/utils/browserSupport';
import {useIdentityContext} from '@fo/shared-providers/IdentityContext';
import MaintenanceMessage from '@fo/components/MaintenanceMessage/MaintenanceMessage';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';

import {LoginStep} from './types';

import styles from './styles';

interface LoginModalInTwoStepsProps {
  showCreateAccount: () => void;
  showForgotPassword: () => void;
  showNonExistentUserModal: () => void;
  onModalClose: () => void;
  withBRSRedirect?: boolean;
}

function LoginModalInTwoSteps({
  onModalClose,
  showCreateAccount,
  showForgotPassword,
  showNonExistentUserModal,
  withBRSRedirect,
}: Readonly<LoginModalInTwoStepsProps>): ReactElement {
  const {formatMessage} = useIntl();
  const {submit, error: identityError, isLoading} = useLogin();
  const [userEmail, setUserEmail] = useState<string>();
  const [currentLoginStep, setCurrentLoginStep] = useState<LoginStep>(LoginStep.EMAIL);

  const queryParams = isBrowser ? new URLSearchParams(window.location.search) : new URLSearchParams('');
  const brsApplicationReference = queryParams.get('application_reference') || undefined;
  const {updateProfile, user} = useIdentityContext();

  const flags = useFlags();
  // For first step
  const handleEmailSubmit = ({emailAddress}: {emailAddress: Login['emailAddress']}) => {
    setUserEmail(emailAddress);
    setCurrentLoginStep(LoginStep.PASSWORD);
    // Focus on the password field for typing
    setTimeout(() => {
      (document.querySelector('#password') as HTMLInputElement)?.focus();
    }, 300);
  };

  const handleStepBackToEmail = () => {
    setCurrentLoginStep(LoginStep.EMAIL);
    setTimeout(() => {
      (document.querySelector('#emailAddress') as HTMLInputElement)?.focus();
    }, 300);
  };

  // second step, actually runs the login function
  const handlePasswordSubmit = async ({password}: {password: Login['password']}, onSuccess: () => void) => {
    if (!userEmail) {
      return;
    }
    const successUser = await submit({emailAddress: userEmail, password});
    if (successUser && successUser.challengeName === IdentityChallenge.NEW_PASSWORD_REQUIRED) {
      navigate(getPageRoute('account.temporaryPassword'));
      return;
    }
    if (successUser) {
      if (withBRSRedirect) {
        updateProfile(user as Partial<SystemOfRecord.AccountHolderDetails>, brsApplicationReference).then(() => {
          redirectByBRSQuery();
        });
      } else {
        redirectByQueryParam(getPageRoute('dashboard.home'));
      }
      onSuccess();
      return;
    }

    // If all else fails
    onModalClose();
    showNonExistentUserModal();
  };

  useEffect(() => {
    if (identityError) {
      onModalClose();
      showNonExistentUserModal();
    }
  }, [identityError]);

  if (flags.pcsReactFeatMaintenanceMode) {
    return (
      <BaseModal onClose={onModalClose}>
        <MaintenanceMessage />
      </BaseModal>
    );
  }

  return (
    <BaseModal onClose={onModalClose}>
      {(onClose) => (
        <div data-testid="modal.loginFormOnTwoSteps" className={cx(styles.container)}>
          <section className={cx(styles.titleContainer)}>
            {currentLoginStep === LoginStep.PASSWORD && (
              <Button
                variant={ButtonVariant.UNSTYLED}
                ariaLabel=""
                className={cx(styles.backButton)}
                onClick={handleStepBackToEmail}
              >
                <ArrowWithLineLargeIcon rotation={IconRotation.CCW_180} />
              </Button>
            )}
            <h2 className={cx(styles.title)}>
              <FormattedMessage id="phoenix.login.title" />
            </h2>
          </section>
          <section className={cx(styles.formStepsContainer)}>
            <div className={cx(styles.formStepsContainerSlider(currentLoginStep))}>
              {/* ********** */}
              {/* EMAIL FORM */}
              {/* ********** */}
              <div>
                <Formik
                  initialValues={{emailAddress: ''}}
                  onSubmit={handleEmailSubmit}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {({handleSubmit}) => (
                    <SinglePageForm
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                      }}
                      submitButton={{
                        id: 'login-email',
                        label: formatMessage({id: 'phoenix.login.form.button.setPassword.text'}),
                        loading: isLoading,
                        testId: 'login.emailAddress',
                      }}
                      afterSubmitContent={
                        <Button
                          ariaLabel={formatMessage({id: 'phoenix.login.form.noAccount'})}
                          onClick={() => {
                            onClose();
                            showCreateAccount();
                          }}
                          testId="ui.loginForm.noAccount"
                          variant={ButtonVariant.DEFAULT_TEXT}
                        >
                          <FormattedMessage id="phoenix.login.form.noAccount" />
                        </Button>
                      }
                      isFormDisabled={currentLoginStep === LoginStep.PASSWORD}
                    >
                      <EmailAddressField<Login>
                        label={formatMessage({id: 'phoenix.fields.email.label'})}
                        name="emailAddress"
                        placeholder={formatMessage({id: 'phoenix.fields.email.placeholder'})}
                        required
                        testId="ui.loginForm.input.email"
                        labelTestId="ui.loginForm.label.email"
                      />
                    </SinglePageForm>
                  )}
                </Formik>
              </div>
              {/* ************* */}
              {/* PASSWORD FORM */}
              {/* ************* */}
              {currentLoginStep === LoginStep.PASSWORD && (
                <div>
                  <Formik
                    initialValues={{password: ''}}
                    onSubmit={(event) => handlePasswordSubmit(event, onClose)}
                    validateOnChange={false}
                    validateOnBlur={false}
                  >
                    {({handleSubmit}) => {
                      let warningMessage;

                      return (
                        <SinglePageForm
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                          }}
                          submitButton={{
                            id: 'login-password',
                            label: formatMessage({id: 'phoenix.login.form.button.text'}),
                            loading: isLoading,
                            testId: 'login.password',
                            onClick: () => track(segmentEvents.fundingCloud.account.loginSubmit),
                          }}
                          afterSubmitContent={
                            <Button
                              ariaLabel={formatMessage({id: 'phoenix.login.form.forgotPassword'})}
                              id="forgotPasswordButton"
                              onClick={() => {
                                if (onModalClose) {
                                  onModalClose();
                                }

                                showForgotPassword();
                              }}
                              type="button"
                              variant={ButtonVariant.DEFAULT_TEXT}
                              testId="ui.loginForm.forgotPassword"
                            >
                              <FormattedMessage id="phoenix.login.form.forgotPassword" />
                            </Button>
                          }
                        >
                          <PasswordField<Login>
                            label={formatMessage({id: 'phoenix.fields.password.label'})}
                            name="password"
                            placeholder={formatMessage({id: 'phoenix.fields.password.placeholder'})}
                            required
                            testId="ui.loginForm.input.password"
                            labelTestId="ui.loginForm.label.password"
                            warningMessage={warningMessage}
                          />
                        </SinglePageForm>
                      );
                    }}
                  </Formik>
                </div>
              )}
            </div>
          </section>
          <section>
            <div className={cx(styles.tideUserSeparator)}>
              <div className={cx(styles.tideUserSeparatorDashes)} />
              <div>
                <FormattedMessage id="phoenix.fields.tideUser.or" />
              </div>
              <div className={cx(styles.tideUserSeparatorDashes)} />
            </div>
            <p className={cx(styles.tideUserSubtitle)}>
              <FormattedMessage id="phoenix.fields.tideUser.subtitle" />
            </p>
            <Button
              ariaLabel={formatMessage({id: 'phoenix.fields.tideUser.continueButton'})}
              id="tideUserContinueButton"
              href={TIDE_WEB_APP_BASE_ADDRESS}
              type="button"
              variant={ButtonVariant.PRIMARY_OUTLINE}
              testId="ui.loginForm.tideUser.continueButton"
            >
              <FormattedMessage id="phoenix.fields.tideUser.continueButton" />
            </Button>
          </section>
        </div>
      )}
    </BaseModal>
  );
}

export default LoginModalInTwoSteps;
